import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ws } from '../lib/websocket';

/**
 * Open a single WS connection subscribed to every board in `boardIds` and
 * invalidate the right React Query caches on incoming events.
 *
 * Using one connection with N subscriptions (rather than N connections) plays
 * nicely with the API — `subscribe` is additive server-side. The subscription
 * set is kept in sync via `ws.setBoards()` so adding a new board doesn't tear
 * down existing live subscriptions.
 */
export function useMultiBoardSubscription(boardIds: string[]) {
  const queryClient = useQueryClient();
  // Stable dependency: serialize ids so the effect only reruns when the *set* changes.
  const key = [...boardIds].sort().join('|');

  useEffect(() => {
    if (boardIds.length === 0) return;

    ws.setBoards(boardIds);

    const unsub = ws.subscribe((msg) => {
      const type = msg.type as string;
      const boardId = (msg as { boardId?: string }).boardId;
      const cardId = (msg as { cardId?: string }).cardId;

      if (type === 'card-updated' || type === 'card-moved') {
        if (boardId) {
          queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
          if (cardId) {
            queryClient.invalidateQueries({ queryKey: ['card', boardId, cardId] });
          }
        } else {
          queryClient.invalidateQueries({ queryKey: ['cards'] });
        }
      }

      if (type === 'activity-created' || type === 'activity-added') {
        if (boardId) {
          queryClient.invalidateQueries({ queryKey: ['cards', boardId] });
        }
      }

      if (type === 'board-updated') {
        queryClient.invalidateQueries({ queryKey: ['boards'] });
        if (boardId) queryClient.invalidateQueries({ queryKey: ['board', boardId] });
      }
    });

    return () => {
      unsub();
      // Leave the socket open — other pages will reset subscriptions via their
      // own ws.connect/setBoards call. Calling setBoards([]) here would emit N
      // unsubscribe messages that the next page's connect() immediately undoes.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, queryClient]);
}
